class SizeGuideScroll extends HTMLElement {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;
  #bindQueued = false;

  connectedCallback() {
    this.#bindTables();
    this.#resizeObserver = new ResizeObserver(() => this.#bindTables());
    this.#resizeObserver.observe(this);

    this.querySelectorAll('details').forEach((details) => {
      details.addEventListener('toggle', () => this.#bindTables());
    });

    this.closest('dialog-component')?.addEventListener('dialog:open', () => {
      requestAnimationFrame(() => this.#bindTables());
    });
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  #bindTables() {
    if (this.#bindQueued) return;
    this.#bindQueued = true;
    requestAnimationFrame(() => {
      this.#bindQueued = false;
      if (!this.isConnected) return;
      this.#bindTablesNow();
    });
  }

  #bindTablesNow() {
    this.querySelectorAll('[data-size-guide-scroll]').forEach((scroller) => {
      if (!(scroller instanceof HTMLElement)) return;
      const wrap = scroller.closest('.size-guide__table-wrap');
      if (!(wrap instanceof HTMLElement)) return;

      const fade = wrap.querySelector('[data-size-guide-fade]');
      const scrollbar = wrap.querySelector('[data-size-guide-scrollbar]');
      const thumb = wrap.querySelector('[data-size-guide-thumb]');
      if (!(scrollbar instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;

      const update = () => this.#sync(scroller, fade, scrollbar, thumb);

      if (!scroller.dataset.sizeGuideBound) {
        scroller.dataset.sizeGuideBound = 'true';
        scroller.addEventListener('scroll', update, { passive: true });
        this.#bindThumbDrag(scroller, thumb);
      }

      update();
    });
  }

  /**
   * @param {HTMLElement} scroller
   * @param {Element | null} fade
   * @param {HTMLElement} scrollbar
   * @param {HTMLElement} thumb
   */
  #sync(scroller, fade, scrollbar, thumb) {
    const overflow = scroller.scrollWidth - scroller.clientWidth > 1;
    if (scrollbar.hidden === overflow) {
      scrollbar.hidden = !overflow;
    }

    if (fade instanceof HTMLElement) {
      const atEnd = scroller.scrollLeft + scroller.clientWidth >= scroller.scrollWidth - 1;
      const hideFade = !overflow || atEnd;
      if (fade.hidden !== hideFade) fade.hidden = hideFade;
    }

    if (!overflow) return;

    const ratio = scroller.clientWidth / scroller.scrollWidth;
    const thumbWidth = Math.max(32, scrollbar.clientWidth * ratio);
    const maxOffset = Math.max(0, scrollbar.clientWidth - thumbWidth);
    const progress = scroller.scrollLeft / (scroller.scrollWidth - scroller.clientWidth);
    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${maxOffset * progress}px)`;
  }

  /**
   * @param {HTMLElement} scroller
   * @param {HTMLElement} thumb
   */
  #bindThumbDrag(scroller, thumb) {
    let dragging = false;
    let startX = 0;
    let startScroll = 0;

    const onMove = (event) => {
      if (!dragging) return;
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const track = thumb.parentElement;
      if (!(track instanceof HTMLElement) || maxScroll <= 0) return;
      const maxOffset = track.clientWidth - thumb.offsetWidth;
      if (maxOffset <= 0) return;
      const delta = event.clientX - startX;
      scroller.scrollLeft = startScroll + (delta / maxOffset) * maxScroll;
    };

    const onUp = () => {
      dragging = false;
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
    };

    thumb.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      dragging = true;
      startX = event.clientX;
      startScroll = scroller.scrollLeft;
      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
    });
  }
}

if (!customElements.get('size-guide-scroll')) {
  customElements.define('size-guide-scroll', SizeGuideScroll);
}
